import React from "react";
import { BUTTON_VARIANTS } from "../../atoms/button/button.shared";
import { Button, type ButtonVariant } from "../../atoms/button/button";
import {
  TEXT_BODY_ELEMENTS,
  TEXT_BODY_VARIANTS,
  TEXT_HEADING_VARIANTS,
  TEXT_LEVELS,
  Text,
  hasMarkup,
  headingRank,
  plainText,
  safeElement,
  safeVariant,
  type TextElement,
  type TextVariant
} from "../../atoms/text/text";
import { LINK_POSITION_BODY } from "../../services/analytics";
import { ctaAriaLabel } from "../../services/cta-aria-label";
import { isSafeHref } from "../../services/safe-href";
import { MarqueeShell } from "./marquee-shell.client";
import styles from "./parallax-marquee.module.css";
import type {
  ParallaxMarqueeAlign,
  ParallaxMarqueeProps,
  ParallaxMarqueeSlide,
  ParallaxMarqueeStage,
  ParallaxMarqueeStripImage,
  ParallaxMarqueeTitlePosition
} from "./parallax-marquee.types";
import { DAY_SECONDS, GLIDE_SECONDS, LONGEST_WAIT_SECONDS } from "./rotation-limits.constants";
import { withSafeRender } from "../../atoms/safe-render";

/** Names the smaller row, the one row in the block a reader meets as a list. */
export const STRIP_LABEL = "More titles";

/** What one title in the row is, for a reader meeting it through its role.
 *  The block itself is the carousel; naming it is the shell's. */
export const SLIDE_ROLE_DESCRIPTION = "slide";

/** The widths the three artwork crops are drawn for; see grid.css. */
const WIDE_MEDIA = "(min-width: 1280px)";
const SQUARE_MEDIA = "(min-width: 735px)";

/** Which width band a copy of the title is drawn in. "" is the ordinary case:
 *  one end at every width, so one copy and no band to limit it to. */
type TitleBand = "" | "wide" | "narrow";

const ALIGNS: Record<ParallaxMarqueeAlign, true> = {
  center: true,
  left: true,
  right: true
};

const SLOTS: Record<ParallaxMarqueeTitlePosition, true> = {
  bottom: true,
  top: true
};

/* Button's own set, not a copy of it: one definition, so a treatment published
   there can never be a value an author sets here and never sees. One is the
   only style any frame in this section draws, an open entry on
   design-conflicts/parallax-marquee.mdx. */
export const CTA_VARIANTS: Record<ButtonVariant, true> = BUTTON_VARIANTS;

/** Which of the two button places a field belongs to, and the word that names
 *  that place in the diagnostics. ⚠ NOT A `ParallaxMarqueeTitlePosition`, which
 *  is an END of the thumbnail — both buttons stand at the bottom one. */
type CtaPlace = "primary" | "secondary";

/** What either place draws where the author asks for nothing. Every button the
 *  section draws is the white one, and it publishes no two-button composition to
 *  take a pairing from, so both places fall back to the same style. Annotated
 *  rather than inferred — `published` takes its type parameter from the
 *  fallback, and a bare literal would narrow the return to that one style. */
export const CTA_FALLBACK: ButtonVariant = "secondary";

const STAGES: Record<ParallaxMarqueeStage, true> = {
  card: true,
  full: true,
  minimum: true,
  revealed: true
};

/** The value if the set lists it, the fallback if not, so a CMS string can never
 *  reach a same-named attribute selector. `hasOwnProperty` rather than a bracket
 *  lookup, which would admit `constructor` and friends. */
function published<T extends string>(
  set: Record<T, true>,
  value: string,
  fallback: T
): T {
  return Object.prototype.hasOwnProperty.call(set, value) ? (value as T) : fallback;
}

/** A text field's content, or "" for anything that is not text with ink in it. */
function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** A list field's entries, or none for anything that is not an array. */
function list<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

/** A button the block will draw, every field of it already judged. */
interface DrawableCta {
  /** The authored accessible name, when one is set. */
  ariaLabel?: string;
  href: string;
  label: string;
  variant: ButtonVariant;
}

/** One button, or nothing where the slot cannot be drawn. `place` names the
 *  slot in the diagnostics; `report` is set on the pass that says them, because
 *  this runs once to draw and once to report. ⚠ An href is CMS content on a
 *  real anchor: `services/safe-href` also refuses `tel:` and `mailto:`. */
function usableCta(
  label: unknown,
  url: unknown,
  variant: unknown,
  name: unknown,
  place: CtaPlace,
  report = false
): DrawableCta | undefined {
  const wording = text(label);
  const destination = text(url);

  /* ⚠ AN UNAUTHORED SLOT IS SILENT, AND THAT IS BOTH FIELDS EMPTY RATHER THAN
     EITHER: a slot with one of them filled in is half a button, which is what
     the two messages below are for. */
  if (wording === "" && destination === "") {
    /* Asked about only where the slot is otherwise empty: with wording or a
       destination filled in, one of the messages below names the gap already,
       and a second about the treatment sends an author to the wrong field. */
    if (report && text(variant) !== "") {
      console.error(
        `[ParallaxMarquee] the ${place} button has a treatment and nothing else, so no button is drawn at all and the treatment shows nowhere. Fill in the label and the destination, or empty the treatment.`
      );
    }

    return undefined;
  }

  if (wording === "") {
    if (report) {
      console.error(
        `[ParallaxMarquee] the ${place} button has no wording, so it is dropped and shows nowhere. Fill in the label, or empty the destination as well.`
      );
    }

    return undefined;
  }

  if (!isSafeHref(destination)) {
    if (report) {
      console.error(
        `[ParallaxMarquee] the ${place} button "${wording}" has no usable destination, so it is dropped. Give it an https, http, tel or mailto address, or one written against directv.com such as "/plans" or "#offers" — an empty one, and a tel: or mailto: one, are all refused.`
      );
    }

    return undefined;
  }

  return {
    ariaLabel: ctaAriaLabel(name),
    href: destination,
    label: wording,
    variant: published(CTA_VARIANTS, text(variant), CTA_FALLBACK)
  };
}

/** The buttons a slide's bottom block draws, in place order. ⚠ TWO PLACES AND
 *  NO LIST: nothing past the second fits across the mobile thumbnail's 281px. */
function slideCtas(slide: ParallaxMarqueeSlide, report = false): DrawableCta[] {
  return [
    usableCta(
      slide.primary_cta_label,
      slide.primary_cta_url,
      slide.primary_cta_variant,
      slide.primary_cta_aria_label,
      "primary",
      report
    ),
    usableCta(
      slide.secondary_cta_label,
      slide.secondary_cta_url,
      slide.secondary_cta_variant,
      slide.secondary_cta_aria_label,
      "secondary",
      report
    )
  ].filter((cta): cta is DrawableCta => !!cta);
}

/** The slides that can be drawn at all. ⚠ THE ARTWORK IS THE SLIDE: a row of
 *  pictures with no names is a published shape, but no artwork is an empty card
 *  with nothing to put in it. */
function renderableSlides(slides: unknown): ParallaxMarqueeSlide[] {
  return list<ParallaxMarqueeSlide>(slides).filter(
    slide => !!slide && text(slide.image_mobile_url) !== ""
  );
}

/** One end's own heading and copy, as the markup draws them. */
interface EndText {
  bodyAs: TextElement;
  bodyVariant: TextVariant;
  headingAs: TextElement;
  headingVariant: TextVariant;
}

/** A slide's text elements and styles, every CMS field already judged.
 *  Called by the render AND the reporter, so the two cannot disagree. */
interface SlideText {
  bottom: EndText;
  subtitleAs: TextElement;
  subtitleVariant: TextVariant;
  titleAs: TextElement;
  titleVariant: TextVariant;
  top: EndText;
}

function endText(
  headingLevel: unknown,
  headingVariant: unknown,
  bodyElement: unknown,
  bodyVariant: unknown
): EndText {
  return {
    bodyAs: safeElement(text(bodyElement), "p", TEXT_BODY_ELEMENTS),
    bodyVariant: safeVariant(text(bodyVariant), "body-2", TEXT_BODY_VARIANTS),
    headingAs: safeElement(text(headingLevel), "h3", TEXT_LEVELS),
    headingVariant: safeVariant(text(headingVariant), "subhead-2", TEXT_HEADING_VARIANTS)
  };
}

function slideText(slide: ParallaxMarqueeSlide): SlideText {
  return {
    bottom: endText(
      slide.bottom_heading_level,
      slide.bottom_heading_variant,
      slide.bottom_body_element,
      slide.bottom_body_variant
    ),
    subtitleAs: safeElement(text(slide.subtitle_element), "p", TEXT_BODY_ELEMENTS),
    subtitleVariant: safeVariant(text(slide.subtitle_variant), "body-2", TEXT_BODY_VARIANTS),
    titleAs: safeElement(text(slide.title_level), "h2", TEXT_LEVELS),
    titleVariant: safeVariant(text(slide.title_variant), "subhead-2", TEXT_HEADING_VARIANTS),
    top: endText(
      slide.top_heading_level,
      slide.top_heading_variant,
      slide.top_body_element,
      slide.top_body_variant
    )
  };
}

/** Says where an end heading does not sit exactly one level below the name.
 *  A p on either side is outside the outline, so it is not compared. */
function reportLevels(slide: ParallaxMarqueeSlide): void {
  const { bottom, titleAs, top } = slideText(slide);
  const titleAt = headingRank(titleAs);

  ([
    ["top", top.headingAs, slide.top_heading_content],
    ["bottom", bottom.headingAs, slide.bottom_heading_content]
  ] as const).forEach(([end, level, heading]) => {
    const headingAt = headingRank(level);

    if (text(heading) === "" || titleAt === undefined || headingAt === undefined) {
      return;
    }

    if (headingAt !== titleAt + 1) {
      console.warn(
        `[ParallaxMarquee] ${end}_heading_level is "${level}" under title_level "${titleAs}", so the outline ${headingAt <= titleAt ? "does not step down" : "skips a level"}. Set ${end}_heading_level to ${titleAt < 6 ? `h${titleAt + 1}` : "p"}, or clear both.`
      );
    }
  });
}

/** The three fields that have to reach the page as headings. */
const HEADING_FIELDS: (keyof ParallaxMarqueeSlide)[] = [
  "title_content",
  "top_heading_content",
  "bottom_heading_content"
];

/** The waits already reported. ⚠ ONCE PER VALUE RATHER THAN PER RENDER: the
 *  block is drawn on the server, where every request is another render. */
const reportedIntervals = new Set<string>();

/** Forgets what has been said, so a suite reads its own message rather than an
 *  earlier test's silence. Nothing in the block itself calls it, and on a
 *  production build it does nothing at all. */
export function forgetReportedIntervals(): void {
  if (process.env.NODE_ENV === "production") {
    return;
  }

  reportedIntervals.clear();
}

/** A wait as an author types it: digits, with or without a decimal part. */
const DECIMAL = /^\d+(\.\d+)?$/;

/** The seconds a wait field holds, or NaN where it holds no plain number.
 *  ⚠ NOT `Number` ALONE, which reads "0x10" and "1e1" as waits nobody typed,
 *  nor `parseFloat`, which would take "3s" for three seconds. */
function authoredSeconds(value: unknown): number {
  if (typeof value === "number") {
    return value;
  }

  const typed = text(value);

  return DECIMAL.test(typed) ? Number(typed) : Number.NaN;
}

/** The wait between titles the author asked for, in seconds, or 0 for a row
 *  that stands still. */
function rotationWait(value: unknown): number {
  const asked = authoredSeconds(value);

  return asked >= GLIDE_SECONDS && asked <= LONGEST_WAIT_SECONDS ? asked : 0;
}

/** Whether the author left the slideshow off: an empty field, which a blok
 *  hands over as any of three values, or a 0 typed into it. */
function asksForNoRotation(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }

  if (typeof value === "number") {
    return value === 0;
  }

  return typeof value === "string" && (value.trim() === "" || authoredSeconds(value) === 0);
}

/** ⚠ THE ONE DIAGNOSTIC PAST THE BUILD GATE, on both its grounds: an unreadable
 *  wait leaves a row that looks like one nobody asked to move, and the message
 *  quotes nothing typed. Empty or 0 is the author asking for no slideshow. */
function reportUnusableRotation(value: unknown): void {
  if (asksForNoRotation(value) || rotationWait(value) > 0) {
    return;
  }

  /* The key this is said once per, and nothing more: it is authored wording,
     which is the whole reason every other message here stays behind the gate. */
  const asked = String(value);

  if (reportedIntervals.has(asked)) {
    return;
  }

  reportedIntervals.add(asked);
  console.error(
    "[ParallaxMarquee] rotation_interval_seconds is not a wait the row can " +
      "use, so nothing moves on its own at all. Give it a number of seconds " +
      `from ${GLIDE_SECONDS} to ${Math.floor(LONGEST_WAIT_SECONDS)} (about ` +
      `${Math.floor(LONGEST_WAIT_SECONDS / DAY_SECONDS)} days), or empty the ` +
      "field to leave the row still."
  );
}

/** What is wrong with a slide, said once per CMS row. ⚠ A HEADING FIELD IS NOT
 *  GUARANTEED TO RENDER AS A HEADING: `Text` draws a `div` wherever the content
 *  carries block markup, and the slide then leaves the page's outline. */
function reportSlideProblems(slides: unknown): void {
  list<ParallaxMarqueeSlide>(slides).forEach(slide => {
    if (!slide) {
      return;
    }

    /* ⚠ THE SUBTITLE IS DRAWN WITH THE NAME AND NOWHERE ELSE, so on a nameless
       slide it is authored copy that reaches no one — the quiet half of the
       same mistake the dropped-slide reports below are about. */
    if (text(slide.title_content) === "" && text(slide.subtitle_content) !== "") {
      console.error(
        "[ParallaxMarquee] a slide has a subtitle but no title, so the " +
          "subtitle shows nowhere — it is only ever drawn under a title. " +
          "Fill in the title, or move the line into the picture's description."
      );
    }

    /* ⚠ THE RAW LIST, so the slides the filter dropped are reported too: the
       worst authoring mistake is the one that drops the whole slide. */
    if (text(slide.image_mobile_url) === "") {
      console.error(
        "[ParallaxMarquee] a slide has no image_mobile_url artwork, so it is " +
          "dropped entirely and shows nowhere. Fill it in, or take the row out."
      );
    }

    /* ⚠ AN EMPTY ONE IS NOT THE AUTHOR CALLING THE PICTURE DECORATION, unlike
       the smaller row's, where that reading is the contract. This is the slide's
       own key art, so an empty one hides content rather than a flourish. */
    if (text(slide.image_alt) === "") {
      console.error(
        text(slide.title_content) === "" ?
          "[ParallaxMarquee] a slide has neither a name nor an artwork " +
            "description, so it announces nothing at all. Describe what is in " +
            "the picture — with no title, that description is the only name " +
            "this slide has." :
          "[ParallaxMarquee] a slide has no artwork description, so its " +
            "picture is marked as decoration and announces nothing. Describe " +
            "what is in it — the name is read out beside it either way, so do " +
            "not type that again."
      );
    }

    HEADING_FIELDS.forEach(field => {
      if (hasMarkup(text(slide[field]))) {
        console.error(
          `[ParallaxMarquee] ${field} carries markup of some kind, and it has ` +
            "to be plain text. Inline markup keeps the heading and block-level " +
            "markup loses it, drawing an ordinary container instead — which " +
            "takes the slide out of the page's outline and leaves the headings " +
            "under it with nothing above them. Neither kind is filtered, so " +
            "nothing here makes markup in this field safe. Move the markup " +
            "into the body copy."
        );
      }
    });

    reportLevels(slide);
    slideCtas(slide, true);
  });
}

/** How many of the smaller row's pictures must stand beyond the seam for the
 *  restart to be invisible. ⚠ TAKEN OFF DESKTOP, where a picture is smallest at
 *  21.458% of the row and four of them leave 168px of the edge empty. */
export const STRIP_COVER_ITEMS = 5;

/** How many times over the smaller row's pictures are drawn. ⚠ TWO SETS ARE
 *  ONLY ENOUGH FOR A LONG ROW: one pass travels exactly one set, so what covers
 *  the row at the restart is everything BEYOND it — hence counted in pictures. */
export function stripSets(count: number): number {
  if (!(count > 0)) {
    return 0;
  }

  return Math.max(2, Math.ceil(STRIP_COVER_ITEMS / count) + 1);
}

/** What one end of a thumbnail draws of the title, and which width this copy is
 *  for. ⚠ THE TITLE IS DRAWN TWICE WHEN THE TWO ENDS DISAGREE — CSS cannot move
 *  an element between parents, and one positioned copy overlaps the buttons. */
function titleFor(
  slot: ParallaxMarqueeTitlePosition,
  wide: ParallaxMarqueeTitlePosition,
  narrow: ParallaxMarqueeTitlePosition,
  title: string
): { band: TitleBand; title: string } {
  /* Compared here and not at the call site: `published` takes its type parameter
     from its fallback alone, so a slot read through it narrows to one literal and
     the compiler reads the comparison against the other as unreachable. */
  if (slot !== wide && slot !== narrow) {
    return {
      band: "",
      title: ""
    };
  }

  return {
    band: wide === narrow ? "" : (wide === slot ? "wide" : "narrow"),
    title
  };
}

/** One copy of the top end: which width it is for, and where it is drawn. */
interface TopCopy {
  /** Drawn after the bottom end rather than before it. */
  after: boolean;
  /** Which width this copy is for; "" when it is the only one. */
  band: TitleBand;
}

/** The copies of the top end a slide draws, in DOM order. ⚠ THE END THE NAME
 *  SITS AT IS DRAWN FIRST wherever the other end has a heading, or that heading
 *  is announced ahead of the name. Both ends are placed by the stylesheet. */
function topCopies(
  wide: ParallaxMarqueeTitlePosition,
  narrow: ParallaxMarqueeTitlePosition,
  headed: boolean
): TopCopy[] {
  const wideAfter = headed && wide === "bottom";
  const narrowAfter = headed && narrow === "bottom";

  if (wideAfter === narrowAfter) {
    return [{
      after: wideAfter,
      band: ""
    }];
  }

  /* Each width takes a copy of its own, the one drawn first carrying the name.
     ⚠ ONLY THE TOP END IS EVER COPIED — the buttons are the bottom end's, and a
     copy of that end would duplicate every anchor in the DOM. */
  return [
    {
      after: false,
      band: wideAfter ? "narrow" : "wide"
    },
    {
      after: true,
      band: wideAfter ? "wide" : "narrow"
    }
  ];
}

interface SlotProps {
  /** Which width this whole copy of the end is for; "" when it is the only one.
   *  Set on a top end drawn twice — see `topCopies`. */
  band: TitleBand;
  /** Where the end's own heading and copy sit, a placement of their own. */
  blockAlign: ParallaxMarqueeAlign;
  /** The end's own copy, under its heading or on its own. */
  body: string;
  /** Where the buttons sit, a placement of their own. Absent where there are
   *  none to place: the top end never draws a button. */
  ctaAlign?: ParallaxMarqueeAlign;
  ctas: DrawableCta[];
  /** The end's own heading, one level below the slide's name by default. */
  heading: string;
  /** Drawn by a title the row is not showing, so out of the picture, out of
   *  the outline and out of the tab order until that title is brought in. */
  hidden: boolean;
  /** Every text element and style the slide draws; this end reads its own. */
  looks: SlideText;
  slot: ParallaxMarqueeTitlePosition;
  /** The line under the title, drawn wherever the title is. */
  subtitle: string;
  /** The slide's title when this end of the thumbnail is one it sits at. */
  title: string;
  titleAlign: ParallaxMarqueeAlign;
  /** Which width this copy of the title is for; "" when it is the only one. */
  titleBand: TitleBand;
}

/** One end of a thumbnail, holding up to three blocks, each with its own
 *  alignment — `data-split` turns "name left, button right" into a row. ⚠ THE
 *  COPY SITS ON THE KEY ART with no scrim, and the artwork is the author's, so
 *  no one figure holds for every upload — the measured worst is on the
 *  conflicts page. */
function Slot({
  band,
  blockAlign,
  body,
  ctaAlign = "left",
  ctas,
  heading,
  hidden,
  looks,
  slot,
  subtitle,
  title,
  titleAlign,
  titleBand
}: SlotProps) {
  const end = looks[slot];
  const hasTitle = title !== "";
  const hasText = heading !== "" || body !== "";
  const hasCtas = ctas.length > 0;

  if (!hasTitle && !hasText && !hasCtas) {
    return null;
  }

  /* The band goes on the SLOT when everything it draws belongs to one width, or
     the width that drops the title is left with an empty padded box over the
     artwork. With anything beside it, the band goes on the title's own block. */
  const bandOnSlot = band !== "" || (titleBand !== "" && !hasText && !hasCtas);
  const slotBand = band !== "" ? band : titleBand;

  /* One alignment reads as one column; two or three stand side by side. Counted
     over what this end draws FROM 735 UP, where alone the attribute is read —
     an empty block or a narrow-band title cannot split a slot. */
  const aligns = new Set<ParallaxMarqueeAlign>();

  if (hasTitle && titleBand !== "narrow") {
    aligns.add(titleAlign);
  }

  if (hasText) {
    aligns.add(blockAlign);
  }

  if (hasCtas) {
    aligns.add(ctaAlign);
  }

  return (
    <div
      className={styles.slot}
      data-band={bandOnSlot ? slotBand : undefined}
      data-slot={slot}
      data-split={aligns.size > 1}
      hidden={hidden}
    >
      {hasTitle && (
        <div
          className={styles.block}
          data-align={titleAlign}
          data-band={bandOnSlot || titleBand === "" ? undefined : titleBand}
        >
          {/* ⚠ THE SLIDE'S FIRST HEADING (h2 unless `title_level` says
              otherwise) — an end heading sits below it, drawn after this one,
              which `topCopies` keeps true. ⚠ Block markup makes `Text` draw a
              div, orphaning every end heading below it. */}
          <Text
            as={looks.titleAs}
            className={[styles.title, looks.titleVariant === "subhead-2" ? styles.titleType : null].filter(Boolean).join(" ")}
            tone="primary"
            variant={looks.titleVariant}
            weight="medium"
          >
            {title}
          </Text>
          {subtitle !== "" && (
            <Text
              as={looks.subtitleAs}
              className={[styles.subtitle, looks.subtitleVariant === "body-2" ? styles.subtitleType : null].filter(Boolean).join(" ")}
              tone="primary"
              variant={looks.subtitleVariant}
            >
              {subtitle}
            </Text>
          )}
        </div>
      )}

      {hasText && (
        /* `data-reveal` marks a block that arrives at the reveal rather than
           with the name. It marks the BLOCK — the subtitle arrives then too and
           is collapsed by its own class, `Text` taking no data attribute. */
        <div className={styles.block} data-align={blockAlign} data-reveal="true">
          {heading !== "" && (
            /* ⚠ THE SAME TREATMENT AS THE SLIDE'S NAME, AT EVERY WIDTH —
               20px/28px medium, specified rather than measured off a frame. An
               end carrying both draws two identical lines. Deliberate. */
            <Text
              as={end.headingAs}
              className={[styles.slotHeading, end.headingVariant === "subhead-2" ? styles.slotHeadingType : null].filter(Boolean).join(" ")}
              tone="primary"
              variant={end.headingVariant}
              weight="medium"
            >
              {heading}
            </Text>
          )}
          {body !== "" && (
            <Text
              as={end.bodyAs}
              className={[styles.slotBody, end.bodyVariant === "body-2" ? styles.slotBodyType : null].filter(Boolean).join(" ")}
              tone="primary"
              variant={end.bodyVariant}
            >
              {body}
            </Text>
          )}
        </div>
      )}

      {hasCtas && (
        <div className={styles.block} data-align={ctaAlign} data-reveal="true">
          <div className={styles.ctas}>
            {ctas.map((cta, index) => (
              /* ⚠ ATTRIBUTES, NOT A PUSH: the container reports only an
                 element carrying these two — a bare anchor reports nothing.
                 `linkAttributes` writes them; a push of our own would count
                 it twice. Its name is the wording unless an aria label is
                 authored. */
              <Button
                ariaLabel={cta.ariaLabel}
                className={styles.cta}
                href={cta.href}
                key={`${index}-${cta.href}`}
                linkAttributes
                linkPosition={LINK_POSITION_BODY}
                size="small"
                variant={cta.variant}
              >
                {cta.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface SlideProps {
  /** The title the block is showing. Anything else is a peek beside it. */
  active?: boolean;
  /** A second drawing of a title the row already carries, stood beyond one end
   *  so the wrap has something at the edges. It never becomes the shown one,
   *  so it draws no copy and none of its buttons. */
  cloned?: boolean;
  /** Announced to nobody. A row of two shows every title twice at once, and
   *  the copy that is not announced is the one beyond the end. */
  muted?: boolean;
  /** Past the peek either side, so it is drawn and not on screen. */
  off?: boolean;
  /** Whether the row can be stepped yet, which is what puts a press on a peek
   *  and the keyboard's way to the same place within reach. */
  operable?: boolean;
  /** Where it stands in the row — the slide's name under the pattern, and the
   *  only thing that says how long the row is. Empty on a row of one, which is
   *  no sequence to have a place in. */
  place: string;
  /** Which place in the row a press on it brings into the middle. Absent on a
   *  row of one, where there is nowhere to go. */
  selects?: number;
  /** Which way this one is held out while it eases in; absent on the active. */
  side?: "after" | "before";
  slide: ParallaxMarqueeSlide;
}

/** One title in the main row, with everything it draws on the artwork. */
function Slide({
  active = false,
  cloned = false,
  muted = false,
  off = false,
  operable = false,
  place,
  selects,
  side,
  slide
}: SlideProps) {
  /* The contract is the blok's, so it is read in the CMS's own spelling here and
     nowhere deeper: everything below this line is named the way the markup and
     the stylesheet name it. */
  const titleSlot = published(SLOTS, text(slide.title_position), "bottom");

  /* Unset OR off the contract, the narrow band follows the wider one. */
  const asked = text(slide.title_position_mobile);
  const narrowSlot = asked === "" ? titleSlot : published(SLOTS, asked, titleSlot);

  const titleAlign = published(ALIGNS, text(slide.title_align), "left");
  const ctaAlign = published(ALIGNS, text(slide.bottom_cta_align), "left");
  const topAlign = published(ALIGNS, text(slide.top_align), "left");
  const bottomAlign = published(ALIGNS, text(slide.bottom_align), "left");
  const title = text(slide.title_content);
  /* What a reader hears of the title: the same parse Text draws, so a tooltip
     tag in it never reaches a name or an announcement as raw markup. */
  const titleName = plainText(title);
  const subtitle = text(slide.subtitle_content);
  const topHeading = text(slide.top_heading_content);
  const topBody = text(slide.top_body_content);
  const bottomHeading = text(slide.bottom_heading_content);
  const bottomBody = text(slide.bottom_body_content);
  const top = titleFor("top", titleSlot, narrowSlot, title);
  const bottom = titleFor("bottom", titleSlot, narrowSlot, title);
  const wide = text(slide.image_desktop_url);
  const square = text(slide.image_tablet_url);

  const ctas = slideCtas(slide);
  const looks = slideText(slide);

  /* Named where there is a sequence to have a place in, and where a reader
     will be told about it — a copy beyond the end is out of the tree. */
  const named = !muted && place !== "";

  /* ⚠ THE PLACE AND THE NAME BOTH, in that order. The place is the pattern's
     own name and the only thing that says how long the row is.
     ⚠ THE ARTWORK'S DESCRIPTION STANDS IN where the slide has no title: it is
     then the only other node in here, so without it the button would announce
     a position and nothing else.
     ⚠ AND THE NAME IS READ TWICE AT THE RESTING STEP, where the peek's own
     button carries it as well: deliberate, because the alternative is a name
     that changes as the reader scrolls, which is worse than a word repeated. */
  const spoken = titleName !== "" ? titleName : plainText(text(slide.image_alt));
  const name = named ? (spoken === "" ? place : `${place}, ${spoken}`) : undefined;

  /* ⚠ camelCase for parity with the sibling and the React 19 runtime this
     ships on: the DOM lowercases the attribute on `setAttribute` regardless of
     spelling, so the rendered `fetchpriority` is unchanged (measured under
     jsdom). react-dom 18.3.1, which jest renders, warns once on the prop name
     — harmless, the value still lands. */
  const priority: { fetchPriority?: "high" } = active ? { fetchPriority: "high" } : {};

  /* Both copies are built together, in DOM order, so the one drawn after the
     bottom end cannot drift from the one drawn before it. */
  const copies = cloned ? [] : topCopies(titleSlot, narrowSlot, topHeading !== "");
  const topEnds = copies.map(copy => ({
    after: copy.after,
    element: (
      <Slot
        band={copy.band}
        blockAlign={topAlign}
        body={topBody}
        ctas={[]}
        heading={topHeading}
        hidden={!active}
        key={`top-${copy.band}`}
        looks={looks}
        slot="top"
        subtitle={subtitle}
        title={copy.after ? "" : top.title}
        titleAlign={titleAlign}
        titleBand={top.band}
      />
    )
  }));

  return (
    <div
      aria-hidden={muted || undefined}
      aria-label={name}
      aria-roledescription={named ? SLIDE_ROLE_DESCRIPTION : undefined}
      className={styles.slide}
      data-active={active}
      data-off={off || undefined}
      /* Read by the shell for what it announces on a step that moves no focus;
         the copy itself stays this side of the boundary. ⚠ THE NAME AND NOT
         THE TITLE: a slide drawn as artwork alone has only its description,
         and an empty attribute here announces a position and nothing else. */
      data-pm-title={spoken}
      data-side={side}
      role={named ? "group" : undefined}
      /* ⚠ NO `tabindex` HERE, AND THAT IS THE POINT. A slide is the floor the
         row hands a focus to where a step takes a reader's own footing away,
         and the shell lends it one for that ask alone — a box focusable at
         rest is one a press can leave a focus on, which ends the slideshow for
         the visit and refuses the browser's drag-to-select with it. */
    >
      {/* Three crops, not one reflowed picture — the frames draw 16:9, 1:1 and
          2:3, which no `object-fit` reaches from a single file. The conditions
          are literals because a media query cannot read a custom property. */}
      {/* ⚠ THE SHOWN TITLE'S PICTURE IS THE BOX THE SHELL MEASURES the stage off;
          `MEASURED_PICTURE` is the other half of the mark. */}
      <picture className={styles.picture} data-pm-measure={active || undefined}>
        {wide !== "" && <source media={WIDE_MEDIA} srcSet={wide} />}
        {square !== "" && <source media={SQUARE_MEDIA} srcSet={square} />}
        {/* ⚠ THE SHOWN TITLE'S ARTWORK IS THE PAGE'S LARGEST PAINT, so it is
            asked for first and every other picture in the row waits. */}
        {/* ⚠ NOT DRAGGABLE, OR A PRESS ON IT STARTS THE BROWSER'S OWN IMAGE
            DRAG and the swipe stops being delivered. ⚠ And the description
            never the name: an `alt` cannot follow which title is shown. */}
        <img
          alt={text(slide.image_alt)}
          className={styles.image}
          draggable={false}
          loading={active ? undefined : "lazy"}
          src={text(slide.image_mobile_url)}
          {...priority}
        />
      </picture>

      {/* Painted only on a neighbour, and eased in with it. */}
      <span aria-hidden="true" className={styles.noise} />

      {/* ⚠ EVERY TITLE'S COPY IS DRAWN AND ALL BUT ONE OF IT IS `hidden`. The
          markup is the server's, so a peek cannot be brought in unless its
          copy is here already — out of the picture and the tab order until. */}
      {topEnds.filter(copy => !copy.after).map(copy => copy.element)}
      {!cloned && (
        <Slot
          band=""
          blockAlign={bottomAlign}
          body={bottomBody}
          ctaAlign={ctaAlign}
          ctas={ctas}
          heading={bottomHeading}
          hidden={!active}
          looks={looks}
          slot="bottom"
          subtitle={subtitle}
          title={bottom.title}
          titleAlign={titleAlign}
          titleBand={bottom.band}
        />
      )}
      {topEnds.filter(copy => copy.after).map(copy => copy.element)}

      {/* ⚠ THE PEEK IS THE CONTROL, which is what makes the tap
          keyboard-operable: no frame draws one, and a real button is reached
          by pointer, by finger and by Tab without a line of script. */}
      {selects !== undefined && (
        <button
          aria-label={`Show ${spoken}`}
          className={styles.select}
          data-pm-selects={selects}
          /* ⚠ THE TITLE DRAWN OFF THE SCREEN IS TAKEN OUT HERE TOO, and not
             left to the rule that hides its box: a control reachable outside
             the strip's clip takes focus to a ring nobody can see. */
          hidden={active || off || !operable}
          /* A muted copy is out of the accessibility tree, so it must be out
             of the tab order with it — its twin carries both. */
          tabIndex={muted ? -1 : undefined}
          type="button"
        />
      )}
    </div>
  );
}

/** Parallax Marquee — the homepage's scroll-driven opening. A big strip of
 *  artwork that starts spanning the viewport and shrinks into a card as the page
 *  scrolls, with a strip of smaller artwork and a pause control beneath it. */
function ParallaxMarqueeImpl({
  rotation_interval_seconds: rotationIntervalSeconds,
  slides,
  stage,
  strip_images: stripImages
}: ParallaxMarqueeProps) {
  /* ⚠ NOT `stage ?? measured`. `published` cannot tell an absent value from one
     off the contract — both come back as the fallback — so the two are told apart
     here, or a stray value would quietly take the measurement's place. */
  const askedStage = text(stage);
  const pinned = askedStage === "" ? undefined : published(STAGES, askedStage, "minimum");

  /* Judged here rather than in the shell so the contract's own spelling stops
     at this file, as the slides' does. 0 is a row that stands still. */
  const rotationSeconds = rotationWait(rotationIntervalSeconds);

  const renderable = renderableSlides(slides);

  /* ⚠ The description is NOT required to survive, unlike the artwork: an empty
     one is the author marking that picture as decoration, which is a choice
     this component has no basis to overrule. */
  const strip = list<ParallaxMarqueeStripImage>(stripImages)
    .filter(image => !!image && text(image.image_url) !== "")
    .map(image => ({
      alt: text(image.image_alt),
      src: text(image.image_url)
    }));

  const total = renderable.length;

  /* ⚠ SAID BEFORE THE EMPTY ROW BAILS, or the one authoring mistake that costs
     every slide is the one nothing reports. Past the gate are the authoring
     messages, which quote back CMS wording. */
  reportUnusableRotation(rotationIntervalSeconds);

  if (process.env.NODE_ENV !== "production") {
    reportSlideProblems(slides);
  }

  if (total === 0) {
    return null;
  }

  /* A copy of a title the row carries stands beyond each end, because every
     frame draws a peek on BOTH sides and the row wraps — without them the
     first title has nothing to its left. One title has nothing to copy. */
  const wraps = total > 1;

  /* ⚠ A ROW OF TWO SHOWS ONE OF ITS TITLES TWICE AT ONCE, so both copies beyond
     the ends are announced to nobody. A longer row never shows a copy beside its
     own original, so there they are peeks like any other. */
  const muted = total === 2;

  /* Where the shown title sits among the row's children, which is what the
     row's offset counts. One further along wherever a copy stands in front. */
  const trackIndex = wraps ? 1 : 0;

  /* ⚠ THE ROW IS STEPPABLE AT THE RESTING STEP AND NOWHERE ELSE: before it a
     peek is still easing in with half of it off the strip. The annotation
     attaches the tap a step earlier; see the conflicts page. */
  const operable = wraps && (pinned ?? "full") === "minimum";

  const place = (index: number) => (wraps ? `${index + 1} of ${total}` : "");

  /* Built as a list rather than as literals: the copies at the ends carry the
     place of the title they stand for, so a press on one goes there. */
  const row = renderable.map((slide, index) => ({
    cloned: false,
    index,
    slide
  }));

  if (wraps) {
    row.unshift({
      cloned: true,
      index: total - 1,
      slide: renderable[total - 1]
    });
    row.push({
      cloned: true,
      index: 0,
      slide: renderable[0]
    });
  }

  /* ⚠ NOT DECORATION, AND NOT HIDDEN: its own CMS list, so it can show titles
     that appear nowhere else (WCAG 1.1.1). A LIST, unlike the row above — loose
     images say neither how many nor where it ends (1.3.1). */
  const smallStrip = strip.length > 0 ? (
    <div className={styles.smallStrip}>
      {/* Named, because the block holds two rows and this is the only one a
          reader meets by list — and told it IS one, because `list-style: none`
          costs Safari the role. ⚠ THE COUNT IS THE AUTHOR'S: seam copies hidden. */}
      <ul
        aria-label={STRIP_LABEL}
        className={styles.stripTrack}
        role="list"
        style={{ "--pm-strip-count": strip.length } as React.CSSProperties}
      >
        {strip.map((image, index) => (
          <li className={styles.stripItem} key={`${index}-${image.src}`}>
            {/* None of this row is drawn until the picture above reaches its
                resting size, and it can be six artworks long. */}
            <img
              alt={image.alt}
              className={styles.stripImage}
              loading="lazy"
              src={image.src}
            />
          </li>
        ))}
        {/* ⚠ THE SEAM, AND WHAT MAKES THE DRIFT ENDLESS: the row travels one
            full set and starts over, so what stands behind has to COVER it —
            see `stripSets`. Hidden, or titles read twice. */}
        {Array.from(
          { length: stripSets(strip.length) - 1 },
          (_, copy) => strip.map((image, index) => (
            <li
              aria-hidden="true"
              className={styles.stripItem}
              data-seam="true"
              key={`seam-${copy}-${index}-${image.src}`}
            >
              <img
                alt=""
                className={styles.stripImage}
                loading="lazy"
                src={image.src}
              />
            </li>
          ))
        )}
      </ul>
    </div>
  ) : undefined;

  /* Both rows are drawn here and handed over already rendered, so the shell
     takes the measurement across the boundary and none of the copy with it. */
  return (
    <MarqueeShell
      pinned={pinned}
      rotationSeconds={rotationSeconds}
      strip={smallStrip}
      total={total}
    >
      {/* ⚠ NO HANDLER IS BOUND HERE AND NONE CAN BE: the row is the server's,
          so the shell finds it by `data-pm-row` and listens for itself. The
          markup below is the whole of the row at every step. */}
      <div
        className={styles.track}
        data-pm-row="true"
        style={{ "--pm-index": trackIndex } as React.CSSProperties}
      >
        {row.map((drawing, at) => (
          <Slide
            active={at === trackIndex}
            cloned={drawing.cloned}
            /* Position and source slide, never the authored text: the CMS
               guarantees nothing unique, a slide need not carry a name at all,
               and the row is rebuilt in a fixed order so neither number moves
               under React. The pair keeps a copy at an end apart from the
               title it stands for. */
            key={`${at}-${drawing.index}`}
            muted={muted && drawing.cloned}
            off={Math.abs(at - trackIndex) > 1}
            operable={operable}
            place={place(drawing.index)}
            selects={wraps ? drawing.index : undefined}
            side={at === trackIndex ? undefined : (at < trackIndex ? "before" : "after")}
            slide={drawing.slide}
          />
        ))}
      </div>
    </MarqueeShell>
  );
}
export const ParallaxMarquee = withSafeRender(ParallaxMarqueeImpl, "ParallaxMarquee");

export default ParallaxMarquee;
